import React, { ReactNode } from "react";
import useTranslation from "../../../../hooks/useTranslation";
import DownloadButton from "../ReactPdfViewer/DownloadButton";
import { ToolbarPill } from "../Toolbar";
import ViewerOverlay from "../ViewerOverlay";
import InvoiceView from "./InvoiceView";
import useXmlDocument from "./useXmlDocument";
import { XmlFormat } from "./types";

interface Props {
    file: string | null;
    fileName?: string;
    format: XmlFormat;
    accountId: number | null;
    documentId: number | null;
    annotations: ReactNode;
    topLeft?: ReactNode;
    topRight?: ReactNode;
    bottomLeft?: ReactNode;
    bottomRight?: ReactNode;
}

function Status({ children, tone = "muted" }: { children: ReactNode; tone?: "muted" | "danger" }) {
    return (
        <div
            role={tone === "danger" ? "alert" : "status"}
            className={`p-8 text-center text-sm ${tone === "danger" ? "text-danger" : "text-ink-muted"}`}
        >
            {children}
        </div>
    );
}

export default function XmlViewer({
    file,
    fileName,
    format,
    accountId,
    documentId,
    annotations,
    topLeft,
    topRight,
    bottomLeft,
    bottomRight,
}: Props) {
    const { t } = useTranslation();
    const state = useXmlDocument({ file, format, accountId, documentId });

    return (
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-surface-2">
            <div className="flex-1 overflow-auto px-8 pb-8 pt-20">
                {state.status === "loading" && <Status>{t("xml_viewer.loading")}</Status>}
                {state.status === "failed" && (
                    <Status tone="danger">{t("xml_viewer.load_error")}</Status>
                )}
                {state.status === "unreadable" && (
                    <Status tone="danger">{t("xml_viewer.parse_error")}</Status>
                )}
                {state.status === "loaded" && <InvoiceView invoice={state.invoice} />}
            </div>
            {annotations}
            <ViewerOverlay topLeft={topLeft} bottomLeft={bottomLeft} bottomRight={bottomRight}>
                {topRight}
                <ToolbarPill>
                    <DownloadButton file={file} fileName={fileName} />
                </ToolbarPill>
            </ViewerOverlay>
        </div>
    );
}
